import { useEffect, useState } from 'react'
import Template from './../Template'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import * as SG from '../../style/Styles'
import * as TXT from './../../util/Texts'
import * as F from './../../util/Functions'
import BlockGame from '../../components/game/BlockGame'
import * as BetService from './../../services/Bet.service'
import * as GameService from './../../services/Game.service'
import { ERROR_ALERT, SUCCESS_ALERT } from '../../util/AlertsMsg'

/**
 * Mapeia cada level do back-end para o rótulo exibido na interface.
 * Levels:
 *  1 = Fase de Grupos (Etapas 1, 2 e 3 — rounds separados)
 *  2 = Segunda Fase (Round of 32) — novo na Copa 2026
 *  3 = Oitavas de Final
 *  4 = Quartas de Final
 *  5 = Semifinal
 *  6 = Terceiro Lugar
 *  7 = Final
 */
const PHASE_LABELS: Record<string, string> = {
  '1_1': TXT.FASE_1,
  '1_2': TXT.FASE_2,
  '1_3': TXT.FASE_3,
  '2_1': TXT.SEGUNDA_FASE,
  '3_1': TXT.OCTAVES,
  '4_1': TXT.QUARTERFINALS,
  '5_1': TXT.SEMIFINAL,
  '6_1': TXT.THIRD_PLACE,
  '7_1': TXT.FINAL,
}

/** Extrai os IDs de todas as partidas de uma fase (sem separadores de data) */
const extractGameIDs = (gamers: any[]): number[] =>
  gamers
    .filter((g: any) => g.separate === false)
    .map((g: any) => g.id)

export default function MyBets() {

  const [fases, setFases]               = useState<any[]>([])
  const [myBets, setMyBets]             = useState<any>([])
  const [lastUp, setLastUp]             = useState<string>('')
  const [reloadMatchs, setReloadMatchs] = useState<boolean>(false)
  const [teams, setTeams]               = useState<string[]>([])

  /**
   * Busca todos os jogos do back e monta o array de fases dinamicamente
   * a partir dos levels e rounds retornados pela API — sem depender de IDs hardcoded.
   */
  const getGamers = async () => {

    const result = await GameService.GetAllGame()

    if (result.data?.status) {

      setTeams(result.data.teams)
      setLastUp(result.data.lastUpdate)

      const betsData: Record<number, Record<number, { matchs: any[] }>> = result.data.bets

      // Monta as fases na ordem correta usando PHASE_LABELS como referência de ordem
      const fasesArr: any[] = []

      Object.keys(PHASE_LABELS).forEach((key) => {
        const [levelStr, roundStr] = key.split('_')
        const level = parseInt(levelStr)
        const round = parseInt(roundStr)

        if (betsData[level]?.[round]) {
          fasesArr.push({
            title:  PHASE_LABELS[key],
            level,
            round,
            gamers: betsData[level][round].matchs,
          })
        }
      })

      setFases(fasesArr)
    }
  }

  /**
   * Verifica se há partidas na fase sem aposta registrada pelo usuário.
   * Usa os IDs vindos diretamente da API — sem gamesIDs.ts.
   * Retorna true se houver jogo em aberto (sem aposta), false se estiver completo.
   */
  const isCompleted = (fase: any): boolean => {

    // Partidas ainda sem times definidos não contam como pendentes
    const validGamers = fase.gamers.filter(
      (g: any) => g.separate === false && g.home_team_id !== 0 && g.visiting_team_id !== 0
    )

    if (validGamers.length === 0) return false

    const phaseGameIDs = extractGameIDs(fase.gamers)

    const bettedIDs = myBets
      .filter((b: any) => phaseGameIDs.includes(b.game_id ?? b.id))
      .map((b: any) => b.game_id ?? b.id)

    return phaseGameIDs.some((id: number) => !bettedIDs.includes(id))
  }

  const getBetUser = async () => {

    const result = await BetService.GetMyBets(F.GetIDUser())

    if (result.data?.status)
      setMyBets(result.data.bets)
  }

  const saveMybets = async () => {

    const data   = { user_id: F.GetIDUser(), bets: myBets }
    const result = await BetService.UpdateMyBets(data)

    if (result.data?.status) {
      SUCCESS_ALERT(result.data.message)
    } else {
      try {
        ERROR_ALERT(result.response.data.message)
      } catch (error) {
        console.warn(error)
        ERROR_ALERT(TXT.WRONG_TRY_AGAIN)
      }
    }
  }

  useEffect(() => {
    if (reloadMatchs) {
      getGamers()
      setReloadMatchs(false)
    }
  }, [reloadMatchs])

  useEffect(() => {
    getBetUser()
    getGamers()
  }, [])

  return (
    <Template>

        <SG.Title style={{ textAlign: 'center' }} className='upperCase'>{TXT.MY_BETS}</SG.Title>

        {lastUp !== '' &&
          <SG.P className='lastUpdateTxt'>{TXT.LAST_RESULT_UPDATED} {lastUp}</SG.P>
        }

        <SG.PADD>
        {fases.length > 0 && fases.map((fase, i) => (
          <Accordion key={i} className='boxBets'>
            <AccordionSummary
              className='AccordionSummary'
              expandIcon={<ExpandMoreIcon style={{ color: 'white', fontSize: 40 }} />}
              aria-controls={`panel${i}a-content`}
              id={`panel${i}a-header`}
            >
              <Typography className='AccordionTitle'>
                {fase.title}
                {isCompleted(fase) &&
                  <SG.Span title={TXT.HAS_MATCH_NO_BET}><SG.I className="fa-solid fa-circle-exclamation"></SG.I></SG.Span>
                }
              </Typography>
            </AccordionSummary>
            <AccordionDetails>

                <SG.BoxBlocks>
                {fase.gamers.length > 0 && fase.gamers.map((game: any, j: any) => {
                  return game.separate === false ? (
                      <BlockGame
                          key={j}
                          id={game.id}
                          infosData={game.infosMatch}
                          teamHome={F.translateTeamName(game.home_team)}
                          teamVisit={F.translateTeamName(game.visiting_team)}
                          iconHome={game.home_team_icon}
                          iconVisit={game.visiting_team_icon}
                          homeID={game.home_team_id}
                          VisitID={game.visiting_team_id}
                          hasResult={game.hasResult}
                          resultMatch={myBets}
                          setResultMatch={setMyBets}
                          isResult={false}
                          teams={teams}
                          setReloadMatchs={setReloadMatchs}
                          passed={game.passed}
                    />) : (
                      <SG.H2 className='titleDivisor' key={j} style={{ width: '100%' }}>{TXT.MATCH} {game.date} - {TXT.MULTIPLIER}: {game.multip}</SG.H2>
                    )
                  })
                }
                </SG.BoxBlocks>

                <SG.Div className='boxBtnBet'>
                  <SG.PrimaryButtom
                    className='btnBets'
                      onClick={saveMybets}
                    >
                      {TXT.SAVE_BET}
                  </SG.PrimaryButtom>
                </SG.Div>

            </AccordionDetails>
          </Accordion>
        ))}
      </SG.PADD>
    </Template>
  )
}